import React from 'react';
import { Package, CheckCircle, Shield, Award, TrendingUp } from 'lucide-react';

export const AmazonBrandRegistry = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-yellow-400 px-4 py-2 rounded-full mb-6">
              <Package className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-semibold">Amazon Seller Protection</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Amazon Brand Registry <span className="text-yellow-400">Enrollment</span>
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Take control of your Amazon listings. Protect your products from hijackers and counterfeiters with Brand Registry enrollment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onOpenPopup}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Enroll Your Brand
              </button>
              <button
                onClick={onOpenPopup}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop"
                alt="Amazon Brand Registry"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Protect Your Amazon <span className="text-red-600">Business</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Selling on Amazon without Brand Registry protection is like leaving your store unlocked at night. Hijackers can steal your buy box, counterfeiters can damage your reputation, and you have limited tools to fight back.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Amazon Brand Registry gives you powerful tools to protect and grow your brand on the world's largest marketplace. With a registered trademark and Brand Registry enrollment, you gain enhanced content options, listing control, and robust anti-counterfeiting tools.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                <div className="font-bold text-gray-900 mb-2">Important Requirement:</div>
                <p className="text-gray-700">
                  You must have a registered trademark to enroll in Amazon Brand Registry. We can help you register your trademark and then enroll you in Brand Registry—complete end-to-end service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Brand Registry <span className="text-red-600">Benefits</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlock powerful tools and protections that transform how you sell on Amazon.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Proactive Brand Protection',
                description: 'Automated brand protection continuously scans for potential infringements and removes counterfeit listings before they harm your brand.'
              },
              {
                icon: CheckCircle,
                title: 'Listing Control',
                description: 'Take ownership of your product listings with the ability to edit titles, descriptions, and images—no more unauthorized changes by third parties.'
              },
              {
                icon: TrendingUp,
                title: 'Enhanced Brand Content',
                description: 'Create visually rich product pages with A+ Content featuring enhanced images, comparison charts, and brand storytelling to boost conversions.'
              },
              {
                icon: Package,
                title: 'Brand Analytics',
                description: 'Access powerful data insights including search term reports, market basket analysis, and demographic information to optimize your strategy.'
              },
              {
                icon: Award,
                title: 'Sponsored Brands Ads',
                description: 'Unlock premium advertising options including headline search ads and video ads to increase visibility and drive traffic to your brand store.'
              },
              {
                icon: Shield,
                title: 'Report Violations',
                description: 'Quickly report and remove intellectual property violations, counterfeit products, and unauthorized sellers with accelerated removal tools.'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-red-100 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Enrollment <span className="text-red-600">Requirements</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">You'll Need:</h3>
                <ul className="space-y-3">
                  {[
                    'Active registered trademark (word mark or design mark)',
                    'Government registration number from USPTO',
                    'List of product categories you sell',
                    'Images showing your trademark on products or packaging'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">We Handle:</h3>
                <ul className="space-y-3">
                  {[
                    'Complete Brand Registry application',
                    'Trademark verification with Amazon',
                    'Account setup and configuration',
                    'Training on using Brand Registry tools',
                    'Ongoing support for brand protection'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Service <span className="text-red-600">Packages</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Brand Registry Only</h3>
                <div className="text-4xl font-bold text-red-600 mb-4">$299</div>
                <p className="text-gray-600 mb-6">Perfect if you already have a registered trademark</p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Complete Brand Registry application',
                    'Trademark verification assistance',
                    'Account setup and configuration',
                    'Basic training on Brand Registry tools',
                    'Email support for 30 days'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onOpenPopup}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-all"
                >
                  Get Started
                </button>
              </div>

              <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-8 shadow-lg ring-4 ring-yellow-400">
                <div className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full inline-block mb-4">
                  <span className="font-bold text-sm">Complete Package</span>
                </div>
                <h3 className="text-2xl font-bold mb-2">Trademark + Brand Registry</h3>
                <div className="text-4xl font-bold mb-4">$499</div>
                <p className="text-white/90 mb-6">Complete protection—trademark registration and Brand Registry enrollment</p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Full trademark registration service',
                    'Comprehensive trademark search',
                    'USPTO filing and monitoring',
                    'Brand Registry enrollment',
                    'Priority processing',
                    'Dedicated account manager',
                    'Advanced training materials',
                    'Ongoing support for 90 days'
                  ].map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onOpenPopup}
                  className="w-full bg-white text-red-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Get Complete Protection
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Enrollment <span className="text-red-600">Process</span>
            </h2>
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: 'Trademark Verification',
                  description: 'We verify your trademark registration status and gather all required information for Amazon Brand Registry enrollment.'
                },
                {
                  step: '02',
                  title: 'Application Submission',
                  description: 'Our team completes the detailed Brand Registry application, including product category selection and trademark documentation.'
                },
                {
                  step: '03',
                  title: 'Amazon Review',
                  description: 'Amazon reviews your application, which typically takes 1-2 weeks. We monitor the status and respond to any requests from Amazon.'
                },
                {
                  step: '04',
                  title: 'Enrollment Confirmation',
                  description: 'Once approved, you gain immediate access to all Brand Registry tools. We provide training and setup assistance to maximize your protection.'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-6 bg-gray-50 p-6 rounded-xl">
                  <div className="bg-red-600 text-white font-bold text-2xl w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Protect Your Amazon Business Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Stop losing sales to hijackers and counterfeiters. Enroll in Brand Registry and take control of your listings.
          </p>
          <button
            onClick={onOpenPopup}
            className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300"
          >
            Start Enrollment Process
          </button>
        </div>
      </section>
    </div>
  );
};